/* eslint-disable max-lines-per-function */
import 'extensions/multi-system/components/x-selectable-card';
import {CAPACITY_FILTER_FIELD, IMAGE_HEIGHT, IMAGE_SIZE, IMAGE_TYPE, IMAGE_WIDTH, INDOOR_CATEGORY_FILTERS, INDOOR_UNIT_DEFAULT_FILTERS, PRODUCT_LISTING_ONDEMANDCOLUMNS, ROOM_CAPACITY_UNIT, StateActions} from 'extensions/multi-system/data/constants';
import {eventStateChange} from 'extensions/multi-system/utils/events';
import 'extensions/ui-components/x-loader';
import 'extensions/ui-components/x-message';
import 'extensions/ui-components/x-product-card';
import 'extensions/ui-components/x-tooltip';
import {ProductListController} from 'extensions/ui-controllers/product-list/lib/product-list';
import {defineCustomElement} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import stylesheet from './x-step-indoor.scss?as=stylesheet';

export class StepIndoor extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			_categoryFilters: {
				type: Array
			},
			loading: {
				state: true,
				type: Boolean
			},
			products: {
				state: true,
				type: Array
			},
			roomCount: {
				attribute: 'room-count',
				type: Number
			},
			rooms: {
				type: Array
			},
			selectedFilter: {
				type: String
			},
			selectedRoom: {
				attribute: 'selected-room',
				type: Number
			}
		};
	}

	get selectedRoomName () {
		return this.rooms?.[this.selectedRoom]?.name;
	}

	get selectedRoomSelection () {
		return this.rooms?.[this.selectedRoom]?.selection;
	}

	get selectedRoomProduct () {
		return this.rooms?.[this.selectedRoom]?.product;
	}

	get filters () {
		return [
			...INDOOR_UNIT_DEFAULT_FILTERS,
			{
				name: 'imagetypes',
				value: {
					sizes: [
						IMAGE_SIZE,
						'original'
					],
					types: [IMAGE_TYPE]
				}
			},
			{
				name: 'search',
				value: [
					{
						field: 'CustomField_Values:customfields:RefrigerantType',
						operator: 'EQ',
						value: 'R32'
					}
				]
			},
			{
				name: 'search',
				value: this.selectedRoomSelection?.class?.map(capacityClass => ({
					field: 'CustomField_Values:customfields:CapacityClass',
					operator: 'EQ',
					value: capacityClass
				}))
			}
		];
	}

	constructor () {
		super();

		// Default values

		/**
		 * @type {array}
		 */
		this._categoryFilters = [];

		/**
		 * @type {boolean}
		 */
		this.loading = true;

		/**
		 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaProduct[]}
		 */
		this.products = [];

		/**
		 * @type {number}
		 */
		this.roomCount = 0;

		/**
		 * @type {import('extensions/multi-system/types').Room[]}
		 */
		this.rooms = [];

		/**
		 * @type {string}
		 */
		this.selectedFilter = 'ALL';

		/**
		 * @type {number}
		 */
		this.selectedRoom = 0;
	}

	async getProducts () {
		await this.updateComplete;

		this.loading = true;

		// Get products
		const productListController = new ProductListController(this, {
			cache: true,
			count: 0,
			filters: this.filters,
			onDemandColumns: [
				...PRODUCT_LISTING_ONDEMANDCOLUMNS,
				'category'
			]
		});

		this.products = await productListController.getProducts();

		// Sort products by capacity
		this.products.sort((a, b) => parseFloat(a?.CustomField_Values?.customfields?.[CAPACITY_FILTER_FIELD]) - parseFloat(b?.CustomField_Values?.customfields?.[CAPACITY_FILTER_FIELD]));

		// Set up categoryFilters
		const filters = [
			{
				code: 'ALL',
				prompt: 'All'
			}
		];

		for (const categoryFilter of INDOOR_CATEGORY_FILTERS) {
			if (this.products?.findIndex((product) => product?.CustomField_Values?.customfields?.category_facet?.indexOf(categoryFilter.code) !== -1) === -1) {
				continue;
			}

			filters.push(categoryFilter);
		}

		this.selectedFilter = 'ALL';
		this._categoryFilters = structuredClone(filters);

		this.loading = false;
	}

	/**
	 * @param {Event} e
	 * @param {string} product
	 */
	handleProductClick (e, product) {
		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: [
				{
					action: StateActions.RoomProductUpdate,
					merge: true,
					payload: {
						capacityClass: product?.CustomField_Values?.customfields?.CapacityClass,
						index: this.selectedRoom,
						product: product?.code
					}
				},
				{
					action: StateActions.OutdoorProductUpdate,
					merge: true,
					payload: void 0
				},
				{
					action: StateActions.RequiredProductsUpdate,
					merge: true,
					payload: void 0
				},
				{
					action: StateActions.UpsellProductsUpdate,
					merge: true,
					payload: void 0
				}
			]
		}));
	}

	handleFilterclick (e, filter) {
		this.selectedFilter = filter.code;
	}

	updated (changedProperties) {
		if (changedProperties.has('selectedRoom')) {
			this.getProducts();
		}
	}

	/**
	 * @param {import('extensions/ui-controllers/product-list').MivaProduct} product
	 */
	renderInventoryMessage (product) {
		if (product.inventory_display_html) {
			return html`
				<div slot="inventory">
					${unsafeHTML(product.inventory_display_html)}
				</div>
			`;
		}

		return nothing;
	}

	renderProducts () {
		if (this.loading) {
			return html`<x-loader class="loader" aspect-ratio-desktop="8/2" aspect-ratio-tablet="8/2" aspect-ratio="8/2"></x-loader>`;
		}

		if (this.products?.length === 0) {
			return html`<x-message type="error">No Units Found.</x-message>`;
		}

		return html`
			<div class="products">
				${this.products?.map(product => this.selectedFilter !== 'ALL' && product?.CustomField_Values?.customfields?.category_facet?.indexOf(this.selectedFilter) === -1 ? nothing : html`
					<x-selectable-card ?selected="${this.selectedRoomProduct === product.code}" @click="${e => this.handleProductClick(e, product)}">
						<x-product-card
							class="product"
							show-details-link
							base-price="${product.base_price}"
							capacity-cooling="${product?.CustomField_Values?.customfields?.CapacityCoolDisplaykW}"
							capacity-heating="${product?.CustomField_Values?.customfields?.CapacityHeatDisplaykW}"
							gas-levy="${product?.CustomField_Values?.customfields?.GasLevyAmount}"
							refrigerant-type="${product?.CustomField_Values?.customfields?.RefrigerantType}"
							category="${product.category?.name}"
							formatted-price="${product.formatted_price}"
							formatted-base-price="${product.formatted_base_price}"
							image-height="${IMAGE_HEIGHT}"
							image-url="${product?.imagetypes[IMAGE_TYPE]?.sizes[IMAGE_SIZE]?.url || product?.imagetypes[IMAGE_TYPE]?.sizes?.original?.url}"
							image-width="${IMAGE_WIDTH}"
							inventory-data="${product?.CustomField_Values?.customfields?.FGInventory}"
							price="${product.price}"
							sku="${product.code}"
							url="${product.url}"
							?hide-pricing="${!window.mivaJS?.Customer_Permissions?.WebstorePricing}"
						>
							${this.renderInventoryMessage(product)}
						</x-product-card>
					</x-selectable-card>
				`)}
			</div>
		`;
	}

	renderFilter (filter) {
		const classes = {
			active: filter.code === this.selectedFilter,
			'product-filters__filter': true
		};

		return html`
			<button class="${classMap(classes)}" @click="${e => this.handleFilterclick(e, filter)}">${filter.prompt}</button>
		`;
	}

	renderFiltersOptions () {
		return this._categoryFilters?.map(categoryFilter => this.renderFilter(categoryFilter));
	}

	renderFilters () {
		const classes = {
			'product-filters': true,
			'product-filters-loading': this.loading
		};

		return html`
			<div class="${classMap(classes)}">
				${this.renderFiltersOptions()}
			</div>
		`;
	}

	render () {
		return html`
			<div class="container">
				<x-section heading="Select an Indoor Unit for ${this.selectedRoomName} (${this.selectedRoomSelection?.display} ${ROOM_CAPACITY_UNIT})">
					${this.renderFilters()}
					${this.renderProducts()}
				</x-section>
			</div>
		`;
	}
};

defineCustomElement('x-step-indoor', StepIndoor);
